import { ERROR_CODE } from '@/lib/config/errorCode';
import { api, ResponseMap, staticDataApi } from '@/lib/utils/api.utils';
import { Language } from '@/i18n/settings';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { uniqBy } from '@/lib/utils/processData.utils';

const requestUrl = process.env.NEXT_PUBLIC_REQUEST_URL;

export type AgentData = {
  address?: string;
  chainId?: number;
  change24?: number;
  createTime?: number;
  id?: string;
  aiNarrative?: string;
  mktCap?: number;
  modifyTime?: number;
  pairAddress?: string;
  source?: string;
  tokenInfo?: string;
  tokenPrice?: number;
  txs24h?: number;
  version?: string;
  vol24h?: number;
  athMktCap?: number;
  mktReturn?: number;
  symbol?: string;
  tokenIcon?: string;
  holdCount?: number;
  signalHoldCount?: number;
  signalTokenPrice?: string;
  signalVol24h?: number;
  signalMktCap?: string;
};

type AgentResponse = {
  messages: AgentData[];
};

type AgentCountResponse = {
  count: number;
};

export async function fetchAgentCount(
  version: string,
  lastId: string,
  mktCapZero: string,
  chainId: string,
): Promise<AgentCountResponse> {
  if (!lastId) {
    return { count: 0 };
  }
  const response = await api.get(
    `${requestUrl}/v1/twmsg/get_ca_new_message_count`,
    {
      searchParams: {
        version: version,
        lastId: lastId,
        mkt_cap_zero_included: mktCapZero,
        chain_id: chainId,
      },
    },
  );

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<AgentCountResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }

  return data.data;
}

export function useAgentNewerCount(
  version: string,
  lastId: string,
  mktCapZero: string,
  chainId: string,
) {
  return useQuery({
    queryKey: ['agentNewerCount', version, lastId, mktCapZero, chainId],
    queryFn: () => fetchAgentCount(version, lastId, mktCapZero, chainId),
    refetchInterval: 5000,
  });
}

export async function fetchAgentList(
  pageNo: number,
  pageSize: number,
  version: string,
  chainId: string,
  mktCapZero?: string,
  minId?: string,
): Promise<AgentResponse> {
  const response = await api.get(`${requestUrl}/v1/twmsg/get_ca_message_list`, {
    searchParams: {
      page_no: pageNo,
      page_size: pageSize,
      version: version,
      min_id: minId || '',
      mkt_cap_zero_included: mktCapZero || '',
      chain_id: chainId,
    },
  });

  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }

  const data = await response.json<ResponseMap<AgentResponse>>();
  if (!data.data) {
    throw new Error(ERROR_CODE.EMPTY_DATA);
  }

  return data.data;
}

export function useAgentList(
  pageNo: number,
  pageSize: number,
  version: string,
  chainId: string,
) {
  return useQuery({
    queryKey: ['agentList', pageNo, pageSize, version, chainId],
    queryFn: () => fetchAgentList(pageNo, pageSize, version, chainId),
    placeholderData: (prev) => prev,
    refetchInterval: 15000,
    retry: 5,
  });
}

export function useInfiniteAgentList(
  pageSize: number,
  version: string,
  chainId: string,
) {
  return useInfiniteQuery({
    queryKey: ['infinite_agent_list', pageSize, version, chainId],
    queryFn: ({ pageParam = 1 }) =>
      fetchAgentList(pageParam, pageSize, version, chainId),
    placeholderData: (prev) => prev,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.messages.length < pageSize) {
        return undefined;
      }
      return allPages.length + 1;
    },
    // select: (data) =>
    //   uniqBy(
    //     data.pages.flatMap((page) => page?.messages ?? []),
    //     'id',
    //   ),
    initialPageParam: 1,
    staleTime: 1000 * 5,
  });
}

type TransactionConfigResponse = {
  chainType: string;
  quickBuyInfo?: {
    [key: string]: string;
  };
  quickSellInfo?: {
    [key: string]: string;
  };
};

export async function getAINarrative(params: { token_address: string }) {
  try {
    const response = await api.get(`${requestUrl}/v1/twmsg/get_ai_narrative`, {
      searchParams: {
        token_address: params.token_address,
      },
    });
    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }
    const data =
      await response.json<
        ResponseMap<{ aiNarrative: string; status: string }>
      >();
    if (!data.data) {
      throw new Error(ERROR_CODE.EMPTY_DATA);
    }
    return {
      message: data.data.aiNarrative,
      status: data.data.status,
    };
  } catch (error) {
    console.error('AI Narrative API Error:', error);
    return {
      message: '',
      status: '0',
    };
  }
}

export function useAINarrative(token_address: string, need?: boolean) {
  return useQuery({
    queryKey: ['aINarrative', token_address],
    queryFn: () => getAINarrative({ token_address }),
    refetchInterval: need ? 10000 : false,
    enabled: need,
  });
}
